// src/components/Splitter.js
import React, { useState, useRef, useEffect } from 'react';

const Splitter = ({
  splitRatio,
  onSplitChange,
  leftContent,
  rightContent,
  viewMode = 'split',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !containerRef.current || viewMode !== 'split') return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newRatio =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Constrain between 20% and 80%
      const constrainedRatio = Math.min(Math.max(newRatio, 20), 80);
      onSplitChange(constrainedRatio);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.classList.remove('select-none');
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.classList.add('select-none');
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onSplitChange, viewMode]);

  const handleMouseDown = (e) => {
    if (viewMode !== 'split') return;
    e.preventDefault();
    setIsDragging(true);
  };

  const leftPercentage = splitRatio;
  const rightPercentage = 100 - splitRatio;
  const showInfo = isDragging || isHovering;

  if (viewMode === 'questions') {
    return <div className="flex-1 overflow-hidden">{leftContent}</div>;
  }

  if (viewMode === 'pdf') {
    return <div className="flex-1 overflow-hidden">{rightContent}</div>;
  }

  return (
    <div className="flex-1 flex overflow-hidden" ref={containerRef}>
      {showInfo && (
        <div className="fixed top-4 right-4 bg-slate-900 bg-opacity-90 text-white px-3 py-2 rounded-lg text-sm z-50 pointer-events-none backdrop-blur-sm">
          {Math.round(leftPercentage)}% | {Math.round(rightPercentage)}%
        </div>
      )}

      {/* Left Panel */}
      <div
        className={`overflow-hidden h-full ${
          isDragging ? 'pointer-events-none' : ''
        }`}
        style={{ width: `calc(${leftPercentage}% - 4px)` }}
      >
        {leftContent}
      </div>

      {/* Splitter Divider */}
      <div
        className="w-2 group relative flex-shrink-0 cursor-col-resize select-none z-10 flex items-center justify-center"
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div
          className={`w-full h-full transition-colors duration-200 ${
            isDragging ? 'bg-blue-200' : 'group-hover:bg-blue-100'
          }`}
        />
        <div
          className={`absolute w-1 h-12 bg-slate-300 rounded-full transition-all duration-200 ease-out ${
            isDragging
              ? 'bg-blue-600 scale-y-110 shadow-lg'
              : 'group-hover:bg-blue-500'
          }`}
        />
      </div>

      {/* Right Panel (containing the iframe) */}
      <div
        className={`overflow-hidden h-full ${
          isDragging ? 'pointer-events-none' : ''
        }`}
        style={{ width: `calc(${rightPercentage}% - 4px)` }}
      >
        {rightContent}
      </div>
    </div>
  );
};

export default Splitter;
